import TickText from "@/components/TickText";
import { Button } from "@/components/ui/button";
import { Divider } from "@/components/ui/divider";
import ExtraItem from "@/features/ExtraItem";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogTrigger, DialogContent } from "@/components/ui/dialog";
import { MouseEventHandler, useEffect, useState, useCallback, useMemo, memo } from "react";
import {
  PriceResultAllExtra,
  PriceResultProps,
  ProtectionProps,
  StationResultProps,
} from "@/lib/type";
import { useSearchParams } from "react-router-dom";
import { HIDDEN_EXTRA_ITEMS, ONE_TIME_CHARGE_EXTRA_ITEMS } from "@/lib/constant";
import { checkAfterHour } from "@/helpers/checkAfterHour";
import { extraPriceWithCountOnly, extraPriceWithRentalDays } from "@/helpers/singleExtraPrice";
import { extraTotalWithCountOnly, extraTotalWithRentalDays } from "@/helpers/totalExtraPrice";
import ReactGA from "react-ga4";
import { formatCurrencyAmount } from "@/lib/utils";
import { useRegion } from "@/context/RegionContext";
import { getRegionConfigByCode } from "@/lib/regionConfig";

interface ChooseProtectionProps {
  vehicleSelected: PriceResultProps;
  protectionSelected?: ProtectionProps;
  extraSelected?: PriceResultAllExtra[];
  onNext: MouseEventHandler;
  onSelectExtras: any;
  MAX_CHARGE_DAYS: Record<string, number>
  rentalDays: number;
  dropOff?:StationResultProps;
  pickUp?:StationResultProps;
  deliveryFeeTotal?: number;
}

const ChooseProtection: React.FC<ChooseProtectionProps> = ({
  vehicleSelected,
  protectionSelected,
  extraSelected,
  onNext,
  onSelectExtras,
  MAX_CHARGE_DAYS,
  rentalDays,
  dropOff,
  pickUp,
  deliveryFeeTotal = 0,
}) => {
  const { region } = useRegion();
  const basicProtectionExcess = getRegionConfigByCode(
    vehicleSelected._region ?? region.code,
  ).protection.basicExcess;
  const [currentProtection, setCurrentProtection] =
    useState(protectionSelected);
    const [currentExtras, setCurrentExtras] =
      useState<PriceResultAllExtra[]>(extraSelected ?? []);
   const [searchParams] = useSearchParams();
  
  // Memoize search params values to prevent unnecessary effect runs
  const searchParamsStr = useMemo(() => searchParams.toString(), [searchParams]);
  const pickUpId = useMemo(() => pickUp?.rw_station_id, [pickUp?.rw_station_id]);
  const dropOffId = useMemo(() => dropOff?.rw_station_id, [dropOff?.rw_station_id]);

  // Log currency check only when component mounts or currency changes
  useEffect(() => {
    console.log(vehicleSelected.pricing.currency, vehicleSelected.pricing.currency === 'MYR ');
  }, [vehicleSelected.pricing.currency]);

  const removeDuplicates = (extras:  PriceResultAllExtra[]) => {
    const uniqueExtras = extras.reduce((acc, current) => {
      const isDuplicate = acc.some(extra => extra.extra_id === current.extra_id);
      if (!isDuplicate) {
        acc.push(current);
      }
      return acc;
    }, [] as PriceResultAllExtra[]);
    return uniqueExtras;
  };

useEffect(() => {

  const pickUpDateObj = new Date(
    Number(searchParams.get("pickupYear") ?? ""),
    Number(searchParams.get("pickupMonth") ?? "") - 1,
    Number(searchParams.get("pickupDay") ?? ""),
    Number(searchParams.get("pickupHour") ?? ""),
    Number( searchParams.get("pickupMinute") ?? "")
  );
  const dropoffDateObj = new Date(
    Number(searchParams.get("dropoffYear") ?? ""),
    Number(searchParams.get("dropoffMonth") ?? "") - 1,
    Number(searchParams.get("dropoffDay") ?? ""),
    Number(searchParams.get("dropoffHour") ?? ""),
    Number( searchParams.get("dropoffMinute") ?? "")
  );
   // Get required extras from car.extras.required
   const requiredExtraIds = vehicleSelected?.extras?.required?.[0]?.split(',') || [];
   
   let tempExtras = [...currentExtras];
   
   // Add all required extras
   requiredExtraIds.forEach(extraId => {
     const requiredExtra = vehicleSelected.all_extras.find(e => e.extra_id === extraId);
     if (requiredExtra) {
       tempExtras.push({ ...requiredExtra, count: 1 });
     }
   });

   
   const allExtras = removeDuplicates(tempExtras);
   setCurrentExtras(allExtras);
   onSelectExtras(allExtras);
}, [searchParamsStr, pickUpId, dropOffId, vehicleSelected.all_extras]);


  // Calculate total amount - memoized to prevent unnecessary recalculations
  const calculateTotal = useCallback(() => {
    // Get required extras from car.extras.required with null check
    const requiredExtraIds = vehicleSelected?.extras?.required?.[0]?.split(',') || [];

    // Filter out required extras since they are included in base price
    const nonRequiredExtras = currentExtras.filter(extra => !requiredExtraIds.includes(extra.extra_id));

    return (((vehicleSelected.pricing.total_with_tax ?? 0)*rentalDays) +
      (currentProtection?.price ?? 0) +
      extraTotalWithCountOnly(nonRequiredExtras.filter((e)=>
        ONE_TIME_CHARGE_EXTRA_ITEMS.includes(e.extra_id)
      ))+
      extraTotalWithRentalDays(nonRequiredExtras.filter((e)=>
        !ONE_TIME_CHARGE_EXTRA_ITEMS.includes(e.extra_id)
      ),rentalDays) +
      deliveryFeeTotal
    );
  }, [vehicleSelected.pricing.total_with_tax, vehicleSelected.extras.required, rentalDays, currentProtection?.price, currentExtras, deliveryFeeTotal]);

  // Track checkout event - memoized to prevent recreation on every render
  const trackCheckoutEvent = useCallback(() => {
    const isDebugMode = import.meta.env.VITE_GA_DEBUG === 'true';
    
    try {
      const total = calculateTotal();
      
      // Validate required parameters - return early if validation fails
      if (!vehicleSelected?.pricing?.currency) {
        console.error('Validation failed: Missing required parameter: currency');
        return false; // Return false to indicate validation failed
      }
      if (typeof total !== 'number' || isNaN(total)) {
        console.error('Validation failed: Invalid total value');
        return false; // Return false to indicate validation failed
      }

      // Debug group start
      isDebugMode && console.group('GA4 Checkout Event Tracking');
      isDebugMode && console.log('Environment:', import.meta.env.VITE_BUILD_ENV);
      isDebugMode && console.log('Debug Mode:', isDebugMode);
      
      // Prepare items array for GA4
      const items = [
        // Vehicle as main item
        {
          item_id: vehicleSelected.group.id,
          item_name: vehicleSelected.group.name,
          item_category: 'Vehicle',
          price: vehicleSelected.pricing.total_with_tax,
          quantity: rentalDays,
          currency: vehicleSelected.pricing.currency
        },
        // Protection if selected
        ...(currentProtection ? [{
          item_id: 'protection',
          item_name: 'Basic Protection',
          item_category: 'Protection',
          price: currentProtection.price,
          quantity: 1,
          currency: vehicleSelected.pricing.currency
        }] : []),
        // Extra items
        ...currentExtras.map(extra => ({
          item_id: extra.extra_id,
          item_name: extra.name,
          item_category: extra.is_insurance ? 'Insurance' : 'Extra',
          price: ONE_TIME_CHARGE_EXTRA_ITEMS.includes(extra.extra_id) 
            ? extraPriceWithCountOnly(extra)
            : extraPriceWithRentalDays(extra, rentalDays),
          quantity: extra.count || 1,
          currency: extra.currency
        }))
      ];

      // Log items details if debug mode
      if (isDebugMode) {
        console.log('Items Breakdown:');
        items.forEach((item, index) => {
          console.group(`Item ${index + 1}: ${item.item_name}`);
          console.log('Category:', item.item_category);
          console.log('Price:', item.price);
          console.log('Quantity:', item.quantity);
          console.groupEnd();
        });
      }

      // Prepare event parameters
      const eventParams = {
        // Standard ecommerce parameters
        currency: vehicleSelected.pricing.currency,
        value: total,
        items,
        // Purchase funnel data
        checkout_step: 4,  // Indicate this is step 4 in the funnel
        // Custom parameters
        custom_pickup_location: pickUp?.name,
        custom_pickup_location_id: pickUp?.rw_station_id,
        custom_dropoff_location: dropOff?.name,
        custom_dropoff_location_id: dropOff?.rw_station_id,
        custom_rental_days: rentalDays,
        custom_protection_selected: !!currentProtection,
        custom_extras_count: currentExtras.length,
        custom_total_extras_value: currentExtras.reduce((sum, extra) => 
          sum + (ONE_TIME_CHARGE_EXTRA_ITEMS.includes(extra.extra_id) 
            ? extraPriceWithCountOnly(extra)
            : extraPriceWithRentalDays(extra, rentalDays)), 0),
        debug_mode: isDebugMode
      };

      // Log event parameters if debug mode
      if (isDebugMode) {
        console.group('GA4 Begin Checkout Event');
        console.log('Event Parameters:', eventParams);
        console.log('Purchase Funnel Step:', 4);
        console.log('Total Items:', items.length);
        console.log('Total Value:', total);
        console.groupEnd();
      }

      // Send event via gtag
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'begin_checkout', eventParams);
        isDebugMode && console.log('Event sent via gtag');
      }

      // Send event via ReactGA
      ReactGA.event('begin_checkout', eventParams);
      isDebugMode && console.log('Event sent via ReactGA');

      isDebugMode && console.groupEnd();
      
      return true; // Return true to indicate successful tracking

    } catch (error) {
      console.error('Error tracking checkout event:', error);
      return false; // Return false to indicate tracking failed
    }
  }, [calculateTotal, vehicleSelected, currentProtection, currentExtras, pickUp, dropOff, rentalDays]);

  // Handle checkout button click - memoized to prevent recreation on every render
  const handleCheckoutClick: MouseEventHandler = useCallback((e) => {
    // Only proceed with checkout if tracking was successful
    const trackingSuccessful = trackCheckoutEvent();
    
    // Always call onNext regardless of tracking success
    // This ensures the user experience isn't affected by analytics
    onNext(e);
  }, [trackCheckoutEvent, onNext]);

  return (
    <>
      <div className="mx-4 md:mx-32">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between font-bold mb-2">
          <p className="text-2xl">Choose your protection & Extra</p>
          <div className="flex flex-row w-fit space-x-3 self-end md:self-auto">
            <div className="flex flex-col text-end text-sm">
              <p className="font-normal">TOTAL</p>
              <p>{formatCurrencyAmount(calculateTotal(), vehicleSelected.pricing.currency)}</p>
            </div>
            <Button className="h-10" onClick={handleCheckoutClick}>
              Go to review & Checkout
            </Button>
          </div>
        </div>
        <p className="font-bold">Protection plans</p>
        <div
          className="border-2 border-green-600 shadow-lg rounded-md p-6 shadow-green-600/20 md:w-1/2"
          style={{ marginTop: "1.2rem", marginBottom: "1.2rem" }}
        >
          <p className="font-bold text-lg">
            Basic{" "}
            <FontAwesomeIcon icon={faStar} className="text-gray-500 fa-xs" />
          </p>
          <p className="font-bold text-sm mb-2">Excess: {formatCurrencyAmount(basicProtectionExcess, vehicleSelected.pricing.currency)}</p>
          <p className="font-bold text-lg mb-6">Included</p>
          <Divider className="mb-3" />
          <TickText
            id={`benefits-${vehicleSelected.group.id}`}
            itemList={["Collision Damage Protection", "Theft Protection"]}
          />
          <div className="flex flex-row items-center justify-end mt-8">
            <Dialog>
              <DialogTrigger className="font-bold w-1/2">
                More Details {`>`}
              </DialogTrigger>
              <DialogContent className="max-w-fit">
                <div className="py-8 px-12">
                  <div className="flex flex-row space-x-3 w-fit items-center mx-auto">
                    <div className="font-bold">
                      <p className="text-lg">Basic</p>
                      <p className="text-sm">Excess: {formatCurrencyAmount(basicProtectionExcess, vehicleSelected.pricing.currency)}</p>
                    </div>
                    <div className="bg-zinc-300 w-0.5 h-10"></div>
                    <p className="text-center text-lg font-bold">Included</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 my-6">
                    <div>
                      <TickText
                        id={`protection-${vehicleSelected.group.id}`}
                        itemList={["Collision Damage Protection"]}
                        textClassName="text-black font-bold"
                      />
                      <p className="text-sm mt-3">
                        Limits your financial liability in the event of
                        collision-related damage to the vehicle to {formatCurrencyAmount(basicProtectionExcess, vehicleSelected.pricing.currency)}.
                      </p>
                    </div>
                    <div>
                      <TickText
                        id={`protection-${vehicleSelected.group.id}2`}
                        itemList={["Theft Protection"]}
                        textClassName="text-black font-bold"
                      />
                      <p className="text-sm mt-3">
                        Limits your financial liability in the event of
                        collision-related damage to the vehicle to {formatCurrencyAmount(basicProtectionExcess, vehicleSelected.pricing.currency)}.
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-row space-x-4 justify-center">
                    <Button variant={"outline"} className="h-12 px-6">
                      Return to your booking
                    </Button>
                    <Button variant={"disabled"} className="h-12 px-6">
                      Selected
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Button variant={"disabled"} className="w-1/3">
              Selected
            </Button>
          </div>
        </div>
      </div>
      <div className="px-4 md:px-32 py-4 bg-neutral-100">
        <p className="font-bold">Protections</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-2">
          {vehicleSelected.all_extras
            .filter((e) =>
              vehicleSelected.extras.available[0]
                .split(",")
                .includes(e.extra_id) && e.is_insurance
            )
            .map((e) => 
              { 
                const price = ONE_TIME_CHARGE_EXTRA_ITEMS.includes(e.extra_id)?extraPriceWithCountOnly(e):extraPriceWithRentalDays(e,rentalDays)
                return (<ExtraItem
                key={`extra${e.extra_id}`}
                extraId={e.extra_id}
                title={e.name}
                content={e.content}
                price={price}
                currency={e.currency}
                onSelect={(count: number) => {
                  let tempExtra = { ...e, count };
                  // Create a new array from currentExtras without filtering
                  let tempNewExtras = [...currentExtras];
                  let existIndex = currentExtras.findIndex((element) => element.extra_id == e.extra_id);
                  if (count === 0) {
                    // If count is 0, remove the item
                    if (existIndex > -1) {
                      tempNewExtras.splice(existIndex, 1);
                    }
                  }else {
                    // For non-zero counts, update or add the item
                    if (existIndex > -1) {
                      tempNewExtras[existIndex] = tempExtra;
                    } else {
                      tempNewExtras.push(tempExtra);
                    }
                  }
                  onSelectExtras(removeDuplicates(tempNewExtras));
                  setCurrentExtras(tempNewExtras);
                }}
                isAdd={currentExtras.findIndex((element) => element.extra_id == e.extra_id) < 0}
                itemCount={currentExtras.findIndex((element) => element.extra_id == e.extra_id) < 0 ? 0 : currentExtras[currentExtras.findIndex((element) => element.extra_id == e.extra_id)].count ?? 0}
                MAX_CHARGE_DAYS={MAX_CHARGE_DAYS}
                otherExtraProps={e}
                rentalDays={rentalDays}
              />)}
            )}
        </div>
      </div>
      <div className="mx-4 md:mx-32 py-4">
        <p className="font-bold">Available extras</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-2">
          {/* Here filter out extra hours or what */}
          {vehicleSelected.all_extras
            .filter((e) =>
              vehicleSelected.extras.available[0]
                .split(",")
                .includes(e.extra_id) && !e.is_insurance &&
                !HIDDEN_EXTRA_ITEMS.includes(e.extra_id)
            )
            .map((e) => {
              const price = ONE_TIME_CHARGE_EXTRA_ITEMS.includes(e.extra_id)?extraPriceWithCountOnly(e):extraPriceWithRentalDays(e,rentalDays)
              return(
              <ExtraItem
                key={`extra${e.extra_id}`}
                extraId={e.extra_id}
                title={e.name}
                content={e.content}
                price={price}
                currency={e.currency}
                onSelect={(count: number) => {
                  let tempExtra = { ...e, count };
                  // Create a new array from currentExtras without filtering
                  let tempNewExtras = [...currentExtras];
                  let existIndex = currentExtras.findIndex((element) => element.extra_id == e.extra_id);
                  if (count === 0) {
                    // If count is 0, remove the item
                    if (existIndex > -1) {
                      tempNewExtras.splice(existIndex, 1);
                    }
                  }else {
                    // For non-zero counts, update or add the item
                    if (existIndex > -1) {
                      tempNewExtras[existIndex] = tempExtra;
                    } else {
                      tempNewExtras.push(tempExtra);
                    }
                  }
                  onSelectExtras(removeDuplicates(tempNewExtras));
                  setCurrentExtras(tempNewExtras);
                }}
                isAdd={currentExtras.findIndex((element) => element.extra_id == e.extra_id) < 0}
                itemCount={currentExtras.findIndex((element) => element.extra_id == e.extra_id) < 0 ? 0 : currentExtras[currentExtras.findIndex((element) => element.extra_id == e.extra_id)].count ?? 0}
                MAX_CHARGE_DAYS={MAX_CHARGE_DAYS}
                otherExtraProps={e}
                rentalDays={rentalDays}
                />
            )})}
        </div>
      </div>
    </>
  );
};

export default memo(ChooseProtection);
